// Russian for the messaging namespace (Module 17 §4, slice P3-B part 2) — the
// design partner's working language: a KazKabel sales manager answers TOO
// ЭлектроМонтаж in Russian, from this inbox.
//
// OFFICE-MESSENGER REGISTER, not generic UI Russian: переписка/входящие for
// the threads, доставлено/прочитано for the ticks, шаблон сообщения for the
// catalog, вебхук for the ingestion log — the words a Russian-speaking sales
// office already uses around Telegram and WhatsApp.
//
// THE FOUR WORDS SETTLED IN BOTH LANGUAGES AT ONCE (full reasoning in
// messaging.i18n.ts, restated because a term settled in one language is not
// settled): thread → «переписка» (the office record; «чат» stays the
// customer's own app surface), unclaimed → «неразобранные», link code →
// «код привязки» (привязка is the handshake; «подключение» stays §4.4's
// bot-connect), WA session window → «окно диалога». Opt-out renders «отказ
// от рассылки» exactly as sales.consent.opted_out does — same fact, same
// customer, same words.
//
// COUNTED NOUNS ARE NOT AGREED WITH THEIR NUMBER (D-030): «Непрочитанных:
// {{count}}», «В очереди: {{count}}» — a colon form that is correct for 1, 2
// and 5 alike, instead of three plural branches a dictionary cannot express.
//
// The type is enforced by `MessagingDict` in messaging.i18n.ts: a missing or
// extra key fails `tsc`, and tools/ci/i18n-parity.ts fails if any value here
// regresses to its English text (the handful that are legitimately identical
// — the products' own names, a pure interpolation — are declared in
// src/i18n-completeness.ts's IDENTICAL_BY_DESIGN.ru).

import type { MessagingDict } from "./messaging.i18n.js";

export const messagingRuDict: MessagingDict = {
  nav: {
    aria: "Разделы сообщений",
    inbox: "Входящие",
    templates: "Шаблоны сообщений",
    settings: "Настройки",
  },

  common: {
    loading: "Загрузка…",
    retry: "Повторить",
    requestId: "Идентификатор запроса",
    serverSaid: "Отклонено: {{title}}",
    close: "Закрыть",
    cancel: "Отмена",
    save: "Сохранить",
    saving: "Сохранение…",
    copied: "Скопировано",
    pageRange: "Показаны {{from}}–{{to}}",
    pageNewer: "Более новые",
    pageOlder: "Более старые",
    pagePrev: "Предыдущая страница",
    pageNext: "Следующая страница",
  },

  access: {
    title: "Недоступно для вашей роли",
    area: "В разделе сообщений нет экранов для вашей роли. Обратитесь к администратору группы.",
    inbox:
      "Входящие — для продаж и администраторов; планирование, финансы и качество читают без ответа.",
    templates: "Шаблоны сообщений — для продаж, планирования, финансов и администраторов.",
    settings: "Настройки сообщений — для администраторов группы и площадки.",
  },

  provider: {
    telegram: "Telegram",
    whatsapp: "WhatsApp",
    // The word sales.channel.email already uses for the same channel.
    email: "Эл. почта",
  },

  offline: {
    title: "Нет сети",
    body: "Нет сети. Страница обновится, когда связь вернётся.",
  },

  age: {
    now: "сейчас",
    min: "{{count}} мин",
    h: "{{count}} ч",
    d: "{{count}} дн",
  },

  inbox: {
    title: "Входящие",
    tabsAria: "Фильтры переписок",
    tabMine: "Мои",
    tabUnassigned: "Без ответственного",
    tabAll: "Все",
    tabSnoozed: "Отложенные",
    tabClosed: "Закрытые",
    tabUnclaimed: "Неразобранные",
    tabHeld: "Непроверенная почта",
    search: "Поиск по перепискам",
    searchHintWithTail: "Имя клиента или контакта, последние цифры ID чата или телефона",
    searchHintNamesOnly: "Имя клиента или контакта",
    listAria: "Переписки",
    unread: "Непрочитанных: {{count}}",
    waWindow: "Окно диалога закроется через {{time}}",
    noPreview: "Сообщений пока нет",
    unknownName: "Неизвестный отправитель",
    emptyTitle: "Переписок пока нет.",
    emptyBody: "Привяжите первого клиента со страницы его контакта.",
    emptyCta: "Открыть клиентов",
    emptyUnassigned: "Всё разобрано",
    emptyUnclaimed: "Неразобранных сообщений нет.",
    emptyFiltered: "Ни одна переписка не подходит под фильтр.",
    error: "Не удалось загрузить переписки",
  },

  thread: {
    pickOne: "Выберите переписку из списка.",
    pickOneBody: "Здесь откроются сообщения и данные клиента.",
    error: "Не удалось загрузить переписку",
    messagesError: "Не удалось загрузить сообщения",
    showEarlier: "Показать более ранние сообщения",
    backToList: "Ко входящим",
    msgStatus: {
      received: "Получено",
      queued: "В очереди",
      sending: "Отправляется",
      sent: "Отправлено",
      delivered: "Доставлено",
      read: "Прочитано",
      failed_retryable: "Сбой — повторим",
      failed_permanent: "Сбой",
      suppressed: "Не отправлено по правилам",
    },
    retry: "Повторить",
    retryFailed: "Не удалось повторить отправку.",
    templateBadge: "Шаблон: {{code}}",
    refused: {
      body_empty: "Не отправлено. {{provider}} показал бы это сообщение пустым.",
      body_too_long:
        "Не отправлено. В тексте {{length}} символов, а {{provider}} принимает не больше 4096.",
      body_markup_invalid:
        "Не отправлено. Telegram не может прочитать разметку этого шаблона. Исправьте его текст в каталоге шаблонов.",
      button_url_invalid:
        "Не отправлено. Ссылка кнопки {{position}} не открывается как адрес. Исправьте её в каталоге шаблонов.",
      stale_bulk:
        "Не отправлено. Это была массовая рассылка, она ждала в очереди больше суток, и её сняли.",
      fallback_rerouted:
        "Не отправлено в этом мессенджере. Сообщение отправлено ещё раз в другом мессенджере клиента.",
      fallback_superseded:
        "Не отправлено. Это была копия для другого мессенджера клиента, и первое сообщение завершилось раньше, чем ушла копия.",
      worker_lost_mid_send: "Отправка прервалась до ответа {{provider}}. Её повторят.",
      account_disconnected:
        "Не отправлено. Аккаунт {{provider}}, через который должно было уйти сообщение, отключили до отправки.",
      account_suspended:
        "Не отправлено. Аккаунт {{provider}}, через который должно было уйти сообщение, приостановлен.",
      caption_too_long:
        "Не отправлено. В тексте {{length}} символов, а {{provider}} принимает с вложениями не больше 1024.",
      media_unavailable:
        "Не отправлено. Вложенный файл больше не хранится, поэтому отправлять было нечего.",
      media_read_failed: "Вложенные файлы сейчас не удалось прочитать. Отправку повторят.",
      lead_not_sent:
        "Не отправлено. Этот файл шёл следом за сообщением, которое не ушло, поэтому отдельно его не отправили.",
    },
    mediaPending: "Получаем вложение…",
    mediaExpired: "Вложение истекло до загрузки. Попросите клиента отправить его ещё раз.",
    mediaUnavailable: "Это вложение показать нельзя",
    mediaOpen: "Открыть вложение",
    valueWithheld: "Цены и ссылки в этом сообщении скрыты для вашей роли.",
    bodyWithheld:
      "В этом сообщении есть цены или ссылки, недоступные вашей роли, поэтому его текст скрыт.",
    forwardedWithheld:
      "Клиент переслал это сообщение из другого чата. Пересланный текст и файлы скрыты для вашей роли, так как в них часто есть цены.",
    credentialWithheld:
      "Ссылка для принятия здесь скрыта: любой, у кого она есть, может принять предложение от имени клиента. Клиент получил её в этом сообщении.",
    attachmentWithheld:
      "В документе, отправленном с этим сообщением, указаны цены, поэтому он скрыт для вашей роли.",
    sender: {
      contact: "Клиент",
      user: "Сотрудник",
      agent: "Агент",
      system: "Бот",
    },
  },

  composer: {
    label: "Ответ",
    placeholder: "Напишите ответ…",
    send: "Отправить",
    sending: "Отправка…",
    sendFailed: "Не удалось отправить сообщение.",
    attach: "Прикрепить файл",
    attachTooLarge: "Файлы больше 25 МБ отправить нельзя.",
    attachTotalTooLarge: "Файлы одного сообщения вместе могут весить не больше 50 МБ.",
    attachLimit: "К одному сообщению можно прикрепить не больше 10 файлов.",
    captionTooLong:
      "С вложениями текст может быть не длиннее 1024 символов. Сейчас в нём {{length}}.",
    attachFailed: "Не удалось загрузить вложение.",
    removeAttachment: "Убрать вложение",
    sessionClosed: "Окно диалога закрыто — выберите утверждённый шаблон",
    templateOpen: "Вставить шаблон",
    templateTitle: "Отправить шаблон",
    templateSearch: "Поиск по шаблонам",
    templateEmpty: "Для этой переписки нет пригодного шаблона.",
    templateSend: "Отправить шаблон",
    templateVarRequired: "Обязательно",
  },

  // Email intake in the inbox (D-327). «Пропустить» for Release: the person
  // lets a held mail through into the conversation; «Удалить» for Discard,
  // because the text is deleted and cannot come back.
  email: {
    senderNamed: "{{name}} <{{address}}>",
    noSubject: "Без темы",
    verified: "Домен отправителя подтверждён",
    notVerified: "Отправитель не подтверждён",
    releasedBy: "Пропущено: {{name}}",
    truncated: "Текст обрезан на 64 КБ",
    earlier: "Предыдущее письмо",
    earlierHidden: "Предыдущее письмо скрыто",
    forwardWithheld:
      "Клиент переслал сюда другое письмо. Его тема и файлы скрыты для вашей роли, так как в них часто есть цены.",
    filesAria: "Файлы письма",
    fileUnnamed: "Файл без имени",
    download: "Скачать",
    downloading: "Готовим скачивание…",
    downloadFailed: "Не удалось скачать файл.",
    heldWaiting: "Ждёт, пока отправителя привяжут к контакту",
    omitted: {
      virus_not_clean: "Не прошёл проверку на вирусы, поэтому не сохранён",
      spam: "Письмо помечено как спам, поэтому файл не сохранён",
      too_large: "Больше 25 МБ, поэтому не сохранён",
      type_not_allowed: "Этот тип файлов мы не принимаем, поэтому он не сохранён",
      bytes_mismatch: "Содержимое не соответствует типу файла, поэтому он не сохранён",
      expired_before_linking: "Срок хранения истёк до того, как отправителя привязали",
    },
    inboundOnly: "Ответьте на это письмо из своей почтовой программы.",
    triageTitle: "Письмо от отправителя, которого нет среди контактов",
    channel: "Канал",
    subject: "Тема",
    sender: "Отправитель",
    nameHoldsAddress: "В имени указан адрес. Настоящий отправитель: {{address}}",
    held: {
      intro: "Здесь ждут письма известных клиентов, чей домен не подтвердился.",
      listAria: "Непроверенная почта",
      received: "Получено {{time}}",
      openThread: "Открыть переписку",
      noCustomer: "Пока не среди контактов",
      release: "Пропустить",
      releasing: "Пропускаем…",
      discard: "Удалить",
      discarding: "Удаляем…",
      releaseTitle: "Пропустить это письмо?",
      releaseBody:
        "Письмо попадёт в переписку отправителя с пометкой «Отправитель не подтверждён». Если отправителя нет среди контактов, оно попадёт в неразобранные.",
      chooseMailbox: "Ящик, с которого пришло письмо",
      chooseMailboxHint:
        "В поле «От» указано несколько ящиков. Выберите тот, за который вы ручаетесь, и письмо будет записано от него.",
      mailboxInvalid: "Выберите ящик, с которого пришло письмо.",
      noMailbox: "Отправителя не удалось прочитать, поэтому письмо можно только удалить.",
      discardTitle: "Удалить это письмо?",
      discardBody: "Текст письма будет удалён, и вернуть его будет нельзя.",
      releaseFailed: "Не удалось пропустить письмо.",
      discardFailed: "Не удалось удалить письмо.",
      decidedElsewhere: "Это письмо уже обработал кто-то другой, поэтому список обновлён.",
      offline: "Нет сети. Кнопки «Пропустить» и «Удалить» заработают, когда она вернётся.",
      empty: "Задержанных писем нет",
      error: "Не удалось загрузить непроверенную почту",
    },
  },

  context: {
    aria: "Контекст переписки",
    open: "Подробности",
    customerTitle: "Клиент",
    noCustomer: "Клиент ещё не привязан.",
    openCustomer: "Открыть клиента",
    attachTitle: "Привязать к клиенту",
    attachSearch: "Поиск контактов",
    attachHint: "Имя контакта или клиента",
    attachNone: "Ни один контакт не подходит.",
    attachConfirm: "Привязать",
    attachFailed: "Не удалось привязать переписку.",
    markSpam: "Спам",
    spamTitle: "Отметить как спам?",
    spamBody: "Переписка закроется, а привязка отправителя будет отозвана.",
    spamFailed: "Не удалось отметить как спам.",
    ordersTitle: "Связанные заказы",
    primaryChip: "Основной",
    primaryOrderOpen: "Открыть заказ",
    ordersError: "Не удалось загрузить заказы.",
    ordersEmpty: "У этого клиента ещё нет заказов.",
    ordersHidden: "Заказы видны отделу продаж и администраторам.",
    setPrimaryOrder: "Сделать основным",
    setPrimaryFailed: "Не удалось назначить основной заказ.",
    identityTitle: "Привязка",
    language: "Язык",
    verifiedAt: "Подтверждено {{date}}",
    pendingIdentity: "Ещё не подтверждено",
    optedOut: "Отказ от рассылки",
    optedOutHard: "Полный отказ от сообщений",
    assignLabel: "Ответственный",
    assignNone: "Не назначен",
    assignMe: "Взять себе",
    assignFailed: "Не удалось назначить ответственного.",
    snoozeLabel: "Отложить",
    snooze2h: "На 2 часа",
    snoozeTomorrow: "До завтра",
    snoozeNextWeek: "До следующей недели",
    snoozedUntil: "Отложено до {{date}}",
    snoozeFailed: "Не удалось отложить переписку.",
    closeThread: "Закрыть переписку",
    reopenThread: "Открыть заново",
    closeFailed: "Не удалось изменить переписку.",
    createQuote: "Новое предложение",
    createQuoteNeedsCustomer: "Чтобы составить предложение, привяжите переписку к клиенту.",
    createDeal: "Новая сделка",
  },

  templates: {
    new: "Новый шаблон",
    createTitle: "Новый шаблон",
    code: "Код",
    codeHint: "snake_case, например order_status_ready",
    name: "Название",
    category: "Категория",
    categories: {
      utility: "Служебные",
      marketing: "Маркетинг",
      authentication: "Аутентификация",
    },
    colCode: "Код",
    colName: "Название",
    colCategory: "Категория",
    colVariants: "Варианты",
    systemChip: "Системный",
    systemHint: "Системный шаблон — его нельзя удалить или отключить.",
    inactiveChip: "Отключён",
    tableCaption: "Шаблоны сообщений",
    showInactive: "Показать отключённые шаблоны",
    inactiveTitle: "Отключённые шаблоны",
    inactiveCaption: "Отключённые шаблоны сообщений",
    inactiveEmpty: "Отключённых шаблонов нет.",
    emptyTitle: "Шаблонов нет",
    emptyBody: "Системные шаблоны создаются при первом использовании.",
    error: "Не удалось загрузить шаблоны",
    createFailed: "Не удалось создать шаблон.",
    updateFailed: "Не удалось сохранить шаблон.",
    readOnly: "Шаблоны редактируют продажи и администраторы.",
    activeLabel: "Действует",
    sendable: "Готов к отправке",
    noVariant: "Варианта нет",
    metaStatus: {
      not_required: "Готов к отправке",
      draft: "Черновик",
      submitted: "На проверке",
      approved: "Утверждён",
      rejected: "Отклонён",
      paused: "Приостановлен",
      disabled: "Отключён",
    },
    rejectedReason: "Ответ Meta: {{reason}}",
    editorTitle: "Редактор вариантов",
    body: "Текст",
    headerText: "Заголовок",
    footerText: "Подпись",
    buttonsTitle: "Кнопки",
    addButton: "Добавить кнопку",
    removeButton: "Убрать кнопку",
    buttonKind: "Тип",
    buttonQuickReply: "Быстрый ответ",
    buttonUrl: "Ссылка",
    buttonLabel: "Надпись",
    buttonUrlField: "URL",
    counter: "{{used}} / {{max}}",
    insertVariable: "Вставить {{key}}",
    preview: "Предпросмотр",
    previewEmpty: "Напишите текст, чтобы увидеть предпросмотр.",
    saveVariant: "Сохранить вариант",
    variantFailed: "Не удалось сохранить вариант.",
    submitMeta: "Отправить в Meta",
    submitParked: "Отправить шаблоны в Meta можно будет после подключения WhatsApp.",
    variablesTitle: "Переменные",
    varKey: "Ключ",
    varSample: "Пример",
    varRequired: "Обязательно",
    addVariable: "Добавить переменную",
    removeVariable: "Убрать переменную",
    variablesHint: "Примеры заполняют предпросмотр и образцы для проверки Meta.",
  },

  // Отказы сервера по полю текста (задача #238): теги и сущности в кавычках —
  // синтаксис самого Telegram, он одинаков на любом языке.
  validation: {
    telegram_html: {
      tag_not_allowed:
        '{{token}} в позиции {{position}} — такой тег Telegram не принимает. Используйте <b>, <i>, <code> или <a href="…"> либо запишите скобки как &lt; и &gt;.',
      bracket_not_escaped:
        "Символ {{token}} в позиции {{position}} не относится к тегу. Запишите его как &lt; или &gt;.",
      ampersand_not_escaped:
        "{{token}} в позиции {{position}} — не сущность, которую принимает Telegram. Одиночный & запишите как &amp;.",
      tag_not_closed: "Тег {{token}} в позиции {{position}} не закрыт.",
      close_without_open:
        "{{token}} в позиции {{position}} закрывает тег, который не открыт, или закрывает его не по порядку.",
      attributes_not_allowed:
        "У тега {{token}} в позиции {{position}} есть атрибуты. Атрибут бывает только у <a>, и это href.",
      link_needs_href: 'Ссылке {{token}} в позиции {{position}} нужен ровно один href="…".',
      link_scheme_not_allowed:
        "Ссылка {{token}} в позиции {{position}} должна начинаться с https://, http://, tg:// или с переменной.",
      tag_space_not_plain:
        "В теге {{token}} в позиции {{position}} стоит особый пробел, который Telegram не читает. Замените его обычным.",
      body_empty: "В тексте нет видимых символов, а пустое сообщение Telegram не отправляет.",
      fallback: "Telegram не сможет прочитать текст в позиции {{position}}.",
      show: "Показать",
    },
    button_url: {
      url_variable_unknown:
        "Ссылка кнопки «{{label}}» использует {{token}}, а такой переменной нет. Добавьте её в переменные или уберите.",
      url_variable_optional:
        "Ссылке кнопки «{{label}}» нужна {{token}}, иначе пустая ссылка ничего не откроет. Сделайте переменную обязательной.",
      url_invalid:
        "Ссылка кнопки «{{label}}» не превращается в адрес, который клиент сможет открыть: {{token}}",
      url_scheme_not_allowed:
        "Ссылка кнопки «{{label}}» должна начинаться с https:// или, в Telegram, с tg://: {{token}}",
      fallback: "Ссылку кнопки «{{label}}» нельзя отправить.",
    },
  },

  settings: {
    error: "Не удалось загрузить настройки",
    accountStatus: {
      connecting: "Подключение",
      active: "Работает",
      webhook_failing: "Сбой вебхука",
      suspended: "Приостановлен",
      disconnected: "Отключён",
    },
    lastWebhook: "Последний вебхук {{time}}",
    neverWebhook: "Вебхуков ещё не было",
    botUsername: "Бот",
    reconnect: "Переподключить",
    probing: "Проверка…",
    probeOk: "Вебхук работает.",
    probeNotOk: "Вебхук не принимает обновления.",
    providerDetail: "Ответ провайдера: {{text}}",
    probeFailed: "Не удалось проверить подключение.",
    disconnect: "Отключить",
    disconnectTitle: "Отключить Telegram?",
    disconnectBody: "Клиенты не смогут писать этому боту, пока его не подключат заново.",
    disconnecting: "Отключение…",
    disconnectFailed: "Не удалось отключить.",
    notConnected: "Telegram ещё не подключён.",
    connectTitle: "Подключить Telegram",
    connectToken: "Токен бота",
    connectTokenHint: "Вставьте токен из @BotFather",
    connectSubmit: "Подключить",
    connecting: "Подключение…",
    connectFailed: "Не удалось подключить бота.",
    copyRequestId: "Копировать идентификатор запроса",
    waParkedTitle: "WhatsApp заработает после проверки Meta",
    waParkedBody:
      "Assessly завершает проверку Meta для WhatsApp. А пока клиенты пишут вам в Telegram.",
    waSignupDisabled: "Подключить WhatsApp можно будет здесь, как только он будет готов.",
    healthTitle: "Состояние доставки",
    healthState: {
      healthy: "В норме",
      degraded: "С перебоями",
      down: "Недоступен",
    },
    queuedCount: "В очереди: {{count}}",
    oldestQueued: "Самое старое в очереди {{time}}",
    errorRate: "Доля ошибок {{pct}}",
    circuitOpened: "Отправка остановлена {{time}}",
    healthEmpty: "Провайдер ещё не подключён.",
    logTitle: "Журнал вебхуков",
    logCaption: "Последние 50 событий вебхука, новые сверху",
    colTime: "Время",
    colKind: "Тип",
    colStatus: "Статус",
    processing: {
      pending: "В обработке",
      processed: "Обработано",
      skipped: "Пропущено",
      error: "Ошибка",
    },
    webhookKind: {
      message: "Сообщение",
      callback_query: "Нажатие кнопки",
      my_chat_member: "Изменение участия",
      other: "Прочее",
    },
    viewPayload: "Показать содержимое",
    payloadTitle: "Содержимое вебхука",
    logEmpty: "Событий вебхука ещё нет.",
    logError: "Не удалось загрузить журнал вебхуков",
    email: {
      title: "Приём почты",
      offBody:
        "Включите приём, чтобы получать письма клиентов на один адрес. Письма известных клиентов попадут в их переписки.",
      turnOn: "Включить",
      turningOn: "Включаем…",
      turnOff: "Выключить",
      turningOff: "Выключаем…",
      address: "Адрес",
      copy: "Копировать адрес",
      forwardLine:
        "Сюда можно переадресовать ящик отдела продаж. Пересланные письма ждут в непроверенной почте, пока домен клиента не подтвердится.",
      turnOffTitle: "Выключить приём почты?",
      turnOffBody:
        "Письма, отправленные на этот адрес, пока приём выключен, не будут получены. Когда вы снова включите приём, адрес останется прежним.",
      unavailable: "Приём почты недоступен в этой системе.",
      enableFailed: "Не удалось включить приём почты.",
      disableFailed: "Не удалось выключить приём почты.",
      intakeOn: "Приём включён",
      intakeOff: "Приём выключен",
      ruleSetActive: "Приём работает",
      ruleSetInactive: "Приём не работает",
      ruleSetUnknown: "Не удалось проверить приём",
      checkedAt: "Проверено {{time}}",
      lastMail: "Последнее письмо {{time}}",
      noMail: "Писем ещё не было",
      probe: "Проверить приём",
      probing: "Проверка…",
      probeOk: "Письма принимаются.",
      probeInactive: "Письма не принимаются. Набор правил приёма не активен",
      probeUnreachable: "Не удалось проверить приём. Повторите через несколько минут.",
      probeFailed: "Не удалось проверить приём.",
      readOnly: "Приём почты включают и выключают администраторы группы и площадки.",
      healthMissing: "Приём ещё не проверялся.",
      error: "Не удалось загрузить настройки приёма почты",
      offline: "Нет сети. Переключатель и проверка заработают, когда она вернётся.",
    },
  },

  link: {
    title: "Сообщения",
    aria: "Привязки мессенджеров",
    notLinked: "Не привязан",
    qrUnavailable: "Ссылку нельзя изобразить QR-кодом; используйте ссылку или код.",
    linkedChip: "Привязан",
    pendingChip: "Ожидает",
    verifiedAt: "Подтверждено {{date}}",
    optedOut: "Отказ от рассылки",
    optedOutHard: "Полный отказ от сообщений",
    generate: "Создать код привязки",
    generating: "Создание…",
    generateFailed: "Не удалось создать код привязки.",
    modalTitle: "Код привязки Telegram",
    codeOnce: "Показывается один раз — скопируйте сейчас.",
    codeAria: "Код привязки",
    deepLink: "Открыть в Telegram",
    qrAria: "QR-код ссылки для Telegram",
    copyCode: "Копировать код",
    expiresIn: "Истекает через {{time}}",
    expired: "Код истёк. Создайте новый.",
    revoke: "Отозвать",
    revokeTitle: "Отозвать привязку?",
    revokeBody:
      "Клиент перестанет получать сообщения по этому каналу, пока его не привяжут заново.",
    revokeReason: "Причина",
    revokeConfirm: "Отозвать",
    revokeFailed: "Не удалось отозвать привязку.",
    waParked: "WhatsApp заработает после проверки Meta. Пока привяжите Telegram.",
    error: "Не удалось загрузить привязки сообщений",
  },
  agent: {
    stripAria: "Агент предложений в этой переписке",
    draftOffer: "Подготовить КП",
    starting: "Запуск…",
    draftChip: "Черновик агента",
    loading: "Проверяем агента предложений…",
    error: "Не удалось прочитать запуск агента.",
  },
};
